import { useState, useMemo, useEffect } from 'react';
import {
  Loader2,
  Package,
  AlertCircle,
  Plus,
  Archive,
  Clock,
  CheckCircle2,
  Receipt,
  ChevronRight,
  Search,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { useClientContext } from '../ClientContext';
import type { LegacyOrder } from '@/api/legacy';
import { useClientOrders, useCreateOrder } from '@/features/legacy/hooks/useOrders';
import { OrderDetailSheet } from '../components/OrderDetailSheet';
import { WaybillsSection } from '@/features/waybills';
import { clientsApi, type ClientForSelector } from '@/api/clients';
import type { OwnershipFilter } from '@shared/types';
import { toast } from 'sonner';

const OWNERSHIP_FILTER_KEY = 'visits-ownership-filter';

function getOwnershipFilter(): OwnershipFilter | undefined {
  try {
    const saved = localStorage.getItem(OWNERSHIP_FILTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed === 'all' || (Array.isArray(parsed) && parsed.length > 0)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return undefined;
}

function OrderCard({
  order,
  onClick,
}: {
  order: LegacyOrder;
  onClick: () => void;
}) {
  const isPaid = order.oplata === 'Да';
  const hasInvoice = order.vipisalinakl === 'Да';

  return (
    <button
      className="w-full p-3 rounded-lg border bg-card border-border text-left hover:bg-muted/50 active:bg-muted transition-colors"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-1">
        <span className="font-medium text-sm flex-1">{order.name}</span>
        <div className="flex items-center gap-1">
          {isPaid && (
            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Оплачен
            </Badge>
          )}
          {hasInvoice && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
              <Receipt className="w-3 h-3 mr-1" />
              Накл.
            </Badge>
          )}
          <ChevronRight className="w-4 h-4 text-muted-foreground ml-1" />
        </div>
      </div>

      {order.comment && (
        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{order.comment}</p>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(order.created).toLocaleDateString('ru-RU')}
        </span>
        <span>{order.createdBy}</span>
      </div>
    </button>
  );
}

export function OrdersTab() {
  const { detail, detailLoading } = useClientContext();
  const [showArchive, setShowArchive] = useState(false);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newOrderName, setNewOrderName] = useState('');
  const [highMargin, setHighMargin] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<LegacyOrder | null>(null);
  const [reorderName, setReorderName] = useState<string | null>(null);
  const [clientSearch, setClientSearch] = useState('');

  const clientName = detail?.client.name || '';

  // Always fetch active orders
  const { data: activeOrders, isLoading: activeLoading, error: activeError } = useClientOrders(
    clientName,
    0
  );

  // Fetch archived orders only when showArchive is true
  const { data: archivedOrders, isLoading: archiveLoading, error: archiveError } = useClientOrders(
    showArchive ? clientName : '',
    1
  );

  const isLoading = activeLoading || (showArchive && archiveLoading);
  const error = activeError || archiveError;

  const createOrder = useCreateOrder();

  // Client picker for "reorder to other"
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter | undefined>();
  useEffect(() => {
    if (reorderName) {
      setOwnershipFilter(getOwnershipFilter());
      setClientSearch('');
    }
  }, [reorderName]);

  const { data: clients = [], isLoading: clientsLoading } = useQuery({
    queryKey: ['clients', 'for-selector', ownershipFilter],
    queryFn: () => clientsApi.getForSelector(ownershipFilter),
    enabled: reorderName !== null,
    staleTime: 60_000,
  });

  const filteredClients = useMemo(() => {
    if (!clients) return [];
    if (!clientSearch.trim()) return clients;
    const search = clientSearch.toLowerCase();
    return clients.filter((c) =>
      c.name.toLowerCase().includes(search) ||
      c.address?.toLowerCase().includes(search)
    );
  }, [clients, clientSearch]);

  const handleReorder = (name: string, company: string) => {
    createOrder.mutate(
      { name, company },
      {
        onSuccess: () => {
          setSelectedOrder(null);
          toast.success('Заказ создан');
        },
      }
    );
  };

  const handleReorderOther = (name: string) => {
    setReorderName(name);
  };

  const handlePickClient = (client: ClientForSelector) => {
    if (!reorderName) return;
    createOrder.mutate(
      { name: reorderName, company: client.name },
      {
        onSuccess: () => {
          toast.success(`Заказ создан для ${client.name}`);
          setReorderName(null);
          setSelectedOrder(null);
        },
      }
    );
  };

  const handleCreateOrder = () => {
    if (!newOrderName.trim() || !clientName) return;
    const orderName = newOrderName.trim() + (highMargin ? ' (п.н.)' : '');
    createOrder.mutate(
      { name: orderName, company: clientName },
      {
        onSuccess: () => {
          setNewOrderName('');
          setHighMargin(false);
          setShowCreateInput(false);
        },
      }
    );
  };

  if (detailLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Загрузка...
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Waybills section - items ready for waybill */}
      {clientName && <WaybillsSection clientName={clientName} />}

      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <Package className="h-4 w-4" />
          Заказы ({activeOrders?.length || 0})
        </h3>
        <div className="flex gap-2">
          <Button
            variant={showArchive ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowArchive(!showArchive)}
          >
            <Archive className="h-4 w-4 mr-1" />
            Архив
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCreateInput(!showCreateInput)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Create order input */}
      {showCreateInput && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              placeholder="Название заказа..."
              value={newOrderName}
              onChange={(e) => setNewOrderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateOrder()}
              disabled={createOrder.isPending}
              autoFocus
            />
            <Button
              size="sm"
              onClick={handleCreateOrder}
              disabled={!newOrderName.trim() || createOrder.isPending}
            >
              {createOrder.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'OK'
              )}
            </Button>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={highMargin}
              onChange={(e) => setHighMargin(e.target.checked)}
              disabled={createOrder.isPending}
              className="rounded border-gray-300"
            />
            <span className="text-muted-foreground">П.н.</span>
          </label>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <AlertCircle className="w-8 h-8 text-destructive mb-2" />
          <p className="text-sm text-muted-foreground">Ошибка загрузки</p>
        </div>
      )}

      {/* Active orders */}
      {!activeLoading && !activeError && (
        <div className="space-y-2">
          {(activeOrders?.length || 0) === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <p className="text-sm">Нет активных заказов</p>
            </div>
          ) : (
            activeOrders?.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onClick={() => setSelectedOrder(order)}
              />
            ))
          )}
        </div>
      )}

      {/* Archived orders */}
      {showArchive && (
        <>
          <div className="flex items-center gap-2 pt-2">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Archive className="h-3 w-3" />
              Архив ({archivedOrders?.length || 0})
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {archiveLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (archivedOrders?.length || 0) === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <p className="text-sm">Нет архивных заказов</p>
            </div>
          ) : (
            <div className="space-y-2">
              {archivedOrders?.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onClick={() => setSelectedOrder(order)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Order detail sheet */}
      <OrderDetailSheet
        order={selectedOrder}
        open={!!selectedOrder}
        onOpenChange={(open) => !open && setSelectedOrder(null)}
        onReorder={handleReorder}
        onReorderOther={handleReorderOther}
      />

      {/* Client picker sheet for "reorder to other" */}
      <Sheet open={reorderName !== null} onOpenChange={(open) => !open && setReorderName(null)}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle>Выбрать клиента</SheetTitle>
            <SheetDescription>Повторить заказ: {reorderName}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск клиента..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            {clientsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : filteredClients.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-4">Клиенты не найдены</p>
            ) : (
              <div className="space-y-1 max-h-[50vh] overflow-y-auto">
                {filteredClients.map((client) => (
                  <button
                    key={client.id}
                    className="w-full text-left p-3 rounded-lg hover:bg-muted/50 active:bg-muted transition-colors"
                    onClick={() => handlePickClient(client)}
                    disabled={createOrder.isPending}
                  >
                    <p className="text-sm font-medium">{client.name}</p>
                    {client.address && (
                      <p className="text-xs text-muted-foreground">{client.address}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
